import { randsToCents } from './formatCurrency';
import type { PaymentProceedNotice, PaymentProceedPayload } from '../components/overlays/shared/PaymentProceedModal';

/** Wallet QR has no extra fee from this local calendar date (matches zapper_qr 0%). */
export const QR_NO_WALLET_FEE_FROM = '2026-09-01';

function localDateIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface ReviewQrPaymentInput {
  hasPendingPayment: boolean;
  merchantName: string;
  baseAmount: number;
  tipAmount: number;
  totalAmount: number;
  reference: string | null;
  referenceRequired?: boolean;
  tipError: string | null;
}

export interface ReviewQrPaymentResult {
  ok: boolean;
  error?: string;
  showConfirmModal: boolean;
  showProceedModal: boolean;
  payload?: PaymentProceedPayload;
}

export function buildQrProceedPayload(input: ReviewQrPaymentInput, asOf: Date = new Date()): PaymentProceedPayload {
  const notices: PaymentProceedNotice[] = [];
  const { baseAmount, tipAmount, totalAmount } = input;

  if (tipAmount > 0) {
    notices.push({
      type: 'info',
      title: `Tip included: R ${tipAmount.toFixed(2)}`,
      description: `Payment R ${baseAmount.toFixed(2)} + tip R ${tipAmount.toFixed(2)} = R ${totalAmount.toFixed(2)}.`,
    });
  }

  if (localDateIso(asOf) >= QR_NO_WALLET_FEE_FROM) {
    notices.push({
      type: 'info',
      title: 'No extra wallet fee',
      description: 'You pay only the till amount. Merchant scan-to-pay has no MyMoolah transaction fee.',
    });
  }

  return {
    amountCents: randsToCents(totalAmount),
    recipientLabel: input.merchantName,
    contextLabel: 'Scan-to-pay / QR network',
    ...(notices.length > 0 ? { notices } : {}),
    ...(input.reference ? { recipientSubLabel: `Ref: ${input.reference}` } : {}),
    title: 'Confirm QR payment',
    proceedButtonText: 'Yes, proceed',
  };
}

/**
 * Pure Review-payment handler. The page must apply the returned modal flags.
 * A successful review closes Confirm Payment and opens the proceed modal so
 * the second dialog is not stacked underneath z-index 9999.
 */
export function reviewQrPayment(input: ReviewQrPaymentInput, asOf: Date = new Date()): ReviewQrPaymentResult {
  if (!input.hasPendingPayment) {
    return { ok: false, showConfirmModal: true, showProceedModal: false };
  }
  if (input.tipError) {
    return {
      ok: false,
      error: input.tipError,
      showConfirmModal: true,
      showProceedModal: false,
    };
  }
  if (input.baseAmount <= 0) {
    return {
      ok: false,
      error: 'Payment amount must be greater than 0.',
      showConfirmModal: true,
      showProceedModal: false,
    };
  }
  if (input.referenceRequired && !(input.reference && String(input.reference).trim())) {
    return {
      ok: false,
      error: 'This merchant requires a payment reference.',
      showConfirmModal: true,
      showProceedModal: false,
    };
  }

  return {
    ok: true,
    showConfirmModal: false,
    showProceedModal: true,
    payload: buildQrProceedPayload(input, asOf),
  };
}

export function cancelQrProceed(): { showConfirmModal: boolean; showProceedModal: boolean } {
  return { showConfirmModal: true, showProceedModal: false };
}
